import { dehydrate, HydrationBoundary } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { Suspense } from 'react';

import { cn } from '@natu/utils/cn';
import { getSlugWithDash } from '@natu/utils/getSlugWithDash';

import { getQueryClient } from '../../../../../../getQueryClient';
import {
  getLinkPropsFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
} from '../../../../../utils';
import { ArticleShowcaseList } from './ArticleShowcaseList';
import { ArticleShowcaseSkeleton } from './ArticleShowcaseSkeleton';
import { getArticleShowcaseQuery, getArticleShowcaseQueryKey } from './articleShowcaseUtils';

const ArticleButton = dynamic(() => import('@natu/ui/Article').then(mod => mod.ArticleButton));

interface SBArticleShowcaseProps {
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  heading?: string;
  subheading?: string;
  buttonLabel?: string;
  buttonLink?: string;
  buttonEvent?: boolean;
  articlesCategory?: string;
  articles?: string[];
  articlesEvent?: boolean;
  limit?: string;
}

export const SBArticleShowcase = ({ blok }: SBProps<SBArticleShowcaseProps>) => {
  const {
    articles: selectedArticles,
    articlesCategory,
    articlesEvent,
    buttonLabel,
    buttonLink,
    buttonEvent,
    heading,
    subheading,
    mbDesktop,
    mbMobile,
    mbTablet,
    mtDesktop,
    mtMobile,
    limit,
    mtTablet,
  } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const resolvedButtonUrl = getLinkPropsFromStoryblok(buttonLink);

  const queryClient = getQueryClient();
  queryClient.prefetchQuery({
    queryKey: getArticleShowcaseQueryKey({
      draftMode: false,
      items: selectedArticles,
      category: articlesCategory,
      perPage: selectedArticles && selectedArticles?.length > 0 ? 100 : Number(limit),
    }),
    queryFn: async () =>
      getArticleShowcaseQuery({
        category: articlesCategory,
        draftMode: false,
        items: selectedArticles,
        perPage: selectedArticles && selectedArticles?.length > 0 ? 100 : Number(limit),
      }),
  });

  return (
    <div className={className} {...sbEditable(blok)}>
      {heading ? (
        <h2 className="text-display-sm-28 xl:text-display-lg-46 mb-4 font-bold lg:mb-5">
          {heading}
        </h2>
      ) : null}
      <div className="flex flex-col gap-y-4 lg:flex-row lg:items-end lg:justify-between lg:gap-x-12">
        {subheading ? <p className="text-lg text-gray-600 xl:text-xl">{subheading}</p> : null}
        {buttonLabel && resolvedButtonUrl && (
          <ArticleButton
            label={buttonLabel}
            event={buttonEvent ? 'E_cta_to_blog' : undefined}
            {...resolvedButtonUrl}
            href={getSlugWithDash(resolvedButtonUrl.href)}
          />
        )}
      </div>
      <HydrationBoundary state={dehydrate(queryClient)}>
        <Suspense fallback={<ArticleShowcaseSkeleton />}>
          <ArticleShowcaseList
            className={cn('mt-10 lg:mt-16')}
            category={articlesCategory}
            selectedArticles={selectedArticles}
            limit={limit ? Number(limit) : 3}
            event={articlesEvent ? 'E_cta_to_blog' : undefined}
          />
        </Suspense>
      </HydrationBoundary>
    </div>
  );
};
